import { inject, Injectable } from '@angular/core'
import { ParticipantService, RegistrationsService } from 'superlikers-microsites-api'
import { firstValueFrom, forkJoin, map } from 'rxjs'

import { CacheService } from '../cache/cache.service'
import { Participant, User, USER_TYPES } from '../../interfaces/participant.interface'

const TAG_PREFIX_ESCUDERIA = 'es_'
const TAG_PREFIX_AGENCIA = 'cd_'

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  #participantService = inject(ParticipantService)
  registrationsService = inject(RegistrationsService)
  #cacheService = inject(CacheService)

  async getCurrentUser() {
    const response$ = this.#participantService.getParticipant<Participant>()
    const response = await firstValueFrom(response$)

    if (!response.participation.authenticated) return null
    return this.userAdapter(response.participation)
  }

  async getEscuderias(): Promise<User[]> {
    const cacheKey = 'users:escuderias'
    const cached = this.#cacheService.getItem<User[]>(cacheKey)
    if (cached) return cached

    const users = await this.fetchUsersByTags([USER_TYPES.ESCUDERIA])
    this.#cacheService.setItem(cacheKey, users)
    return users
  }

  async getAgenciasByEscuderia(escuderia: User): Promise<User[]> {
    const cacheKey = `users:agencias:escuderia:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<User[]>(cacheKey)
    if (cached) return cached

    const pages = this.getPages(escuderia)
    const allAgencias = await this.fetchUsersByTags([USER_TYPES.AGENCIA], pages)
    const users = allAgencias.filter(user => user.escuderia === escuderia.escuderia)
    this.#cacheService.setItem(cacheKey, users)
    return users
  }

  async getUsersByAgencia(agencia: User): Promise<User[]> {
    const cacheKey = `users:agencia:${agencia.agencia}`
    const cached = this.#cacheService.getItem<User[]>(cacheKey)
    if (cached) return cached

    const pages = this.getPages(agencia)
    const agenciaTag = `${TAG_PREFIX_AGENCIA}${agencia.agencia}`
    const allUsers = await this.fetchUsersByTags([agenciaTag], pages)
    const users = allUsers.filter(
      user => user.userType !== USER_TYPES.AGENCIA && user.userType !== USER_TYPES.ESCUDERIA
    )
    this.#cacheService.setItem(cacheKey, users)
    return users
  }

  async getUsersByEscuderia(escuderia: User): Promise<User[]> {
    const cacheKey = `users:escuderia:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<User[]>(cacheKey)
    if (cached) return cached

    const pages = this.getPages(escuderia)
    const escuderiaTag = `${TAG_PREFIX_ESCUDERIA}${escuderia.escuderia}`
    const allUsers = await this.fetchUsersByTags([escuderiaTag], pages)
    const users = allUsers.filter(
      user => user.userType !== USER_TYPES.AGENCIA && user.userType !== USER_TYPES.ESCUDERIA
    )
    this.#cacheService.setItem(cacheKey, users)
    return users
  }

  private async fetchUsersByTags(tags: string[], pages = 1) {
    const requests$ = []
    for (let i = 1; i <= pages; i++) {
      const request$ = this.#participantService
        .getList<Participant>({ page: i, with_admins: true, tags })
        .pipe(map(response => response.data.filter(user => user.staff !== 'OK' && user.state !== 'banned')))

      requests$.push(request$)
    }

    const response$ = forkJoin(requests$)
    const response = await firstValueFrom(response$)

    const users = response.flat().map(this.userAdapter)
    return users
  }

  private userAdapter(participant: Participant): User {
    const { tags } = participant

    const escuderiaTag = tags.find(tag => tag.startsWith(TAG_PREFIX_ESCUDERIA))
    const agenciaTag = tags.find(tag => tag.startsWith(TAG_PREFIX_AGENCIA))
    const zonaTag = tags.find(tag => tag.startsWith('zona_'))

    const escuderia = escuderiaTag?.replace(TAG_PREFIX_ESCUDERIA, '')
    const agencia = agenciaTag?.replace(TAG_PREFIX_AGENCIA, '')
    const agenciaName = agencia?.replace(`_${escuderia}`, '')?.replace('_', ' ')

    const userType = tags.find(tag => Object.values(USER_TYPES).includes(tag as USER_TYPES))

    const defaultAvatar = 'https://secure.gravatar.com/avatar/f7219bbd71f453e41750fa3c63a11da1?size=64&default=mm'

    return {
      uid: participant.uid,
      authenticated: participant.authenticated ?? null,
      avatar: participant.avatar || defaultAvatar,
      name: participant.name,
      email: participant.email,
      cargo: participant.cargo || null,
      escuderiaName: escuderia!,
      agenciaName: agenciaName || null,
      zona: zonaTag || null,
      totalUsers: Number(participant.total) || 0,
      escuderia: escuderia!,
      agencia: agencia!,
      tags,
      userType: userType as USER_TYPES,
      points: participant.points,
      unconfirmed_email: participant.unconfirmed_email,
      celular: participant.celular || '',
      numero_de_documento: participant.numero_de_documento || '',
      update_file: participant.update_file,
      total_points: participant.total_points || 0
    }
  }

  async updateAvatar(url: string) {
    const data = { avatar: url }

    const response$ = this.registrationsService.updateParticipant(data)
    const response = await firstValueFrom(response$)

    return response
  }

  async updateUser(data: Record<string, unknown>) {
    const response$ = this.registrationsService.updateParticipant(data)
    const response = await firstValueFrom(response$)

    return response
  }

  private getPages(user: User) {
    const limit = 30
    const total = user.totalUsers || 0

    const pages = Math.ceil(total / limit)
    return pages
  }
}
